import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addRows,
  pageSelectionState,
  SelectableRow,
  togglePage,
  toggleRow,
} from "../utils/row-selection";

/**
 * Odoo-style list selection: survives paging, shift-click selects a range on
 * the current page, and the selected rows are kept for totals and bulk actions.
 */
export function useRowSelection<T extends SelectableRow>(rows: T[]) {
  const [selection, setSelection] = useState<Map<string, T>>(() => new Map());
  const anchorRef = useRef<number | null>(null);

  // A range anchor only means something on the page it was set on.
  useEffect(() => {
    anchorRef.current = null;
  }, [rows]);

  const { all: allPageSelected, some: somePageSelected } = useMemo(
    () => pageSelectionState(selection, rows),
    [selection, rows],
  );

  const toggle = useCallback(
    (index: number, shiftKey = false) => {
      const anchor = anchorRef.current;
      anchorRef.current = index;
      setSelection((prev) => toggleRow(prev, rows, index, anchor, shiftKey));
    },
    [rows],
  );

  const toggleAll = useCallback(() => {
    anchorRef.current = null;
    setSelection((prev) => togglePage(prev, rows));
  }, [rows]);

  const select = useCallback((more: T[]) => setSelection((prev) => addRows(prev, more)), []);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setSelection(new Map());
  }, []);

  const selectedRows = useMemo(() => Array.from(selection.values()), [selection]);

  return {
    selectedRows,
    count: selection.size,
    isSelected: (id?: string | null) => !!id && selection.has(id),
    allPageSelected,
    somePageSelected,
    toggle,
    toggleAll,
    select,
    clear,
  };
}
